import { useMemo, useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useAuth } from '../../../context/AuthContext';
import { useToast } from '../../../context/ToastContext';
import { AuthService } from '../../../services/auth.service';
import { isWidgetVisible } from '../registry';
import { isGatedModule } from '../registry/moduleMeta';
import { useDashboardAccess } from '../useDashboardAccess';
import useDashboardLayout from './useDashboardLayout';
import WidgetCard from './WidgetCard';
import AddWidgetDialog from './AddWidgetDialog';

const SPAN: Record<string, number> = { sm: 4, md: 6, lg: 12 };

interface Props {
    /** The layout key of the host page: store, commercial, sales-home or personal. */
    boardKey: 'store' | 'commercial' | 'sales-home' | 'personal';
}

/**
 * Appended to the four fixed home pages (add-only: nothing above it changes). It reads the same
 * layout chain as the configurable board, with an EMPTY built-in preset, so a person nobody has
 * customised sees only the heading row and an "Add widget" button. Widgets an admin or head pinned
 * show here with a lock; the viewer still needs each widget's own permission (a layout grants no data).
 */
export default function MyWidgetsSection({ boardKey }: Props) {
    const { hasPermission } = useAuth();
    const toast = useToast();
    const institutionId = AuthService.getSelectedInstitution() ?? '';
    const { enabledModules } = useDashboardAccess();
    const { order, widgets, locked, add, remove } = useDashboardLayout({
        institutionId, boardKey, fallbackIds: [], editable: !!institutionId,
        onError: e => toast.showApiError?.(e, 'Could not save your widgets'),
    });
    const [addOpen, setAddOpen] = useState(false);
    const visible = useMemo(
        () => widgets.filter(def =>
            (!isGatedModule(def.module) || enabledModules.has(def.module)) && isWidgetVisible(def, hasPermission)),
        [widgets, enabledModules, hasPermission],
    );

    return (
        <Box data-testid="my-widgets-section" data-help-section="dashboard.myWidgets" sx={{ mt: 3, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                <Typography variant="h6" fontWeight={700}>My widgets</Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Button data-help="dashboard.addWidget" size="small" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>Add widget</Button>
            </Stack>
            {visible.length > 0 && (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 2 }}>
                    {visible.map(def => (
                        <Box key={def.id} sx={{ gridColumn: { xs: 'span 12', md: `span ${SPAN[def.size]}` } }}>
                            <WidgetCard def={def} institutionId={institutionId} editable locked={locked.includes(def.id)} onRemove={() => remove(def.id)} />
                        </Box>
                    ))}
                </Box>
            )}
            <AddWidgetDialog
                open={addOpen}
                hasPermission={hasPermission}
                present={order}
                enabledModules={Array.from(enabledModules)}
                onAdd={add}
                onClose={() => setAddOpen(false)}
            />
        </Box>
    );
}
